import { Link } from "react-router-dom";
import { getProjects } from "../api";
import { ProjectQuestions, emptyAnswers } from "../ProjectQuestions";
import { Chips, FormHeader, QCard, ZoneChips } from "./Apply";
import { routes } from "../routes";
import { useAsync } from "../useAsync";
import { Pane } from "../ui";

/**
 * The project form as a student sees it, on its own URL.
 *
 * 🔴 A LINK, NOT A PANEL (owner's call, 2026-09-09). The Form tab used to END in a full
 * non-interactive copy of the form, below the configuration and below the question editor —
 * three tall things on one screen, the third of which restated the second. A teacher checking
 * what they are about to open now opens it.
 *
 * 🔴 IT RENDERS THROUGH THE STUDENT'S OWN CONTROLS, which the panel it replaces did not. That
 * panel injected console-styled primitives while its caption said "Exactly what a student
 * sees" — so the one claim it made about itself was the one thing it got wrong.
 * ⚠️ AND IT WENT WRONG AGAIN THE MOMENT THE STUDENT'S FORM WAS REDESIGNED (2026-09-09). The
 * student got one card per question through `QCard`/`Chips`/`ZoneChips`; this page went on
 * importing the INTAKE round's `Q`/`Pills`/`Other` and heading its card "Your project" — the
 * heading handoff `8a` deletes by name. Same claim, same failure, one design apart. It shares
 * `FormHeader` with the student page now, so the header cannot drift at all.
 *
 * 🔴 IT IS NOT `/apply/<id>/project`, AND IT MUST NOT BE. That page resolves an APPLICANT, and
 * a teacher is not on the student roster — [X] which is exactly what the owner was shown: a
 * refusal saying the account is not on this cohort's Classroom roster, where the form should
 * have been. It also renders nothing at all while the form is shut, which is when a preview
 * is most wanted. Nothing here reaches `/api/apply`: the questions come from the teacher
 * payload this console already loads, so `tests/test_applicant_auth.py`'s rule that no route
 * outside that router resolves an applicant, and none inside it resolves a teacher, is
 * untouched.
 *
 * ⚠️ NO SIDEBAR, because the point is what the form looks like rather than where it sits in
 * this console. The way back is a link, and the browser's own back button.
 *
 * ⚠️ THE COHORT ARRIVES AS A PROP, NOT FROM `useParams`. `Cohort` has already checked the id
 * in the address bar against the ones this teacher actually teaches — a course they do not
 * teach, or a base64 id pasted from a Classroom URL, is redirected rather than rendered — and
 * reading the raw parameter here would walk straight past that. It also does not depend on a
 * param inherited across a nested `Routes`.
 */
export function ProjectFormPreviewPage({ courseId }: { courseId: string }) {
  const q = useAsync(() => getProjects(courseId), [courseId]);

  return (
    // ⚠️ NO PAGE HERO, BECAUSE THE STUDENT HAS NONE. It printed the cohort's name above a card
    // that prints the cohort's name, and it was a `--brand-band` strip — dark in both themes,
    // which is right for the wordmark band and wrong for a heading.
    <div className="apply-pad stack">
      <div className="apply-body">
        <div className="stack">
          {/* ⚠️ IT SAYS IT IS A PREVIEW, EVERY TIME. Drawn with the student's controls on a
              page with no console chrome, it is otherwise indistinguishable from the real
              thing — and a teacher who typed an answer into it would reasonably believe they
              had seen it saved. Every control here is disabled and nothing writes. */}
          <p className="alert info" role="status">
            Preview. This is the form students are shown, drawn with their own controls.
            Nothing typed here is saved; the questions are edited on the
            {" "}<Link to={routes.teams(courseId, "questions")}>Questions tab</Link>.
          </p>
          <Pane q={q}>
            {(d) => (
              // ⚠️ THE INTRO GOES THROUGH `Markdown`, LIKE THE STUDENT PAGE. As plain
              // text a teacher writing `**bold**` saw the asterisks here and bold on the
              // form — on the one screen whose whole claim is "this is what a student sees".
              <div className="pform">
                <FormHeader name={d.course.name} closed={d.state !== "open"} closesOn=""
                            count={d.student_form.questions.length}
                            intro={d.student_form.intro} />
                {/* ⚠️ EVERY QUESTION, ALWAYS — the preview is read-only by definition.
                    `disabled` became a per-question predicate so the student's form can agree
                    with the server about what stays editable after teams are out; here the
                    answer is the same for all of them. */}
                <ProjectQuestions form={d.student_form}
                                  draft={emptyAnswers(d.student_form)}
                                  disabled={() => true}
                                  set={() => {}} Q={QCard} Pills={Chips} Other={ZoneChips} />
              </div>)}
          </Pane>
          <p className="small muted">
            <Link to={routes.teams(courseId, "questions")}>← Back to the questions</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
